"use client";

import { useState, useRef } from "react";
import { Check, ChevronDown, Palette } from "lucide-react";
import { cn } from "@/lib/utils";

export default function TradingTools({
    indicators,
    onToggleIndicator,
    handleColorChange: parentHandleColorChange,
    handleOptionChange: parentHandleOptionChange,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [colorPickerOpen, setColorPickerOpen] = useState(null);

    const handleColorChange = (indicator, colorOption, e) => {
        parentHandleColorChange(indicator, colorOption, e.target.value);
    };

    const handleOptionChange = (indicator, option, e) => {
        const newOptions = indicator.options;
        newOptions[option.id].value = e.target.value;
        indicator.options = newOptions;
        parentHandleOptionChange(indicator);
    };

    const toggleColorPicker = (indicatorId) => {
        setColorPickerOpen(colorPickerOpen === indicatorId ? null : indicatorId);
    };

    const indicatorsObject = Object.fromEntries(indicators);
    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-1 bg-[#2c2f33] hover:bg-[#3a3f45] px-3 py-1 rounded text-sm"
            >
                <span>Indicators</span>
                <ChevronDown size={14} className={cn("transition-transform", isOpen && "rotate-180")} />
            </button>

            {isOpen && (
                <div className="absolute top-full right-0 mt-1 w-72 bg-[#1e2124] border border-gray-700 rounded-md shadow-lg z-10">
                    <div className="p-2">
                        <h3 className="text-[#f5c518] text-xs font-medium mb-2">Technical Indicators</h3>
                        <div className="space-y-2">
                            {Object.entries(indicatorsObject).map(([key, indicator]) => {
                                const sortedOptions = (indicator?.options ?? []).sort((a, b) => a.id.localeCompare(b.id));

                                return (
                                    <div key={key}>
                                        <div className="space-y-1">
                                            <div className="flex items-center justify-between">
                                                <label className="flex items-center space-x-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="hidden"
                                                        checked={indicator.enabled || false}
                                                        onChange={() => onToggleIndicator(key)}
                                                    />
                                                    <div
                                                        className={cn(
                                                            "w-4 h-4 rounded border flex items-center justify-center",
                                                            indicator.enabled ? "bg-blue-600 border-blue-600" : "border-gray-600"
                                                        )}
                                                    >
                                                        {indicator.enabled && <Check size={12} />}
                                                    </div>
                                                    <span>{key}</span>
                                                </label>
                                                <div className="flex items-center">
                                                    <span className="text-xs text-gray-400 mr-2">{indicator.description}</span>
                                                    {indicator.colorOptions.length > 0 && (
                                                        <button
                                                            onClick={() => toggleColorPicker(key)}
                                                            className="p-1 rounded hover:bg-[#3a3f45]"
                                                            title="Change color"
                                                        >
                                                            <Palette size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {colorPickerOpen === key &&
                                                indicator.colorOptions.length &&
                                                indicator.colorOptions.map((colorOption) => (
                                                    <>
                                                        <span className="text-xs text-gray-400">{colorOption.label}:</span>
                                                        <input
                                                            type="color"
                                                            value={colorOption.color}
                                                            onChange={(e) => handleColorChange(indicator, colorOption, e)}
                                                            className="w-6 h-6 rounded cursor-pointer"
                                                        />
                                                    </>
                                                ))}
                                        </div>
                                        <div className="space-y-1">
                                            {Object.entries(sortedOptions).map(([key, option]) => {
                                                switch (option.type) {
                                                    case "input":
                                                        return (
                                                            <div key={key}>
                                                                <span className="text-xs text-gray-400">{option.label}:</span>
                                                                <input
                                                                    type="text"
                                                                    value={option.value}
                                                                    onChange={(e) => handleOptionChange(indicator, option, e)}
                                                                />
                                                            </div>
                                                        );
                                                    case "select":
                                                        return (
                                                            <div key={key}>
                                                                <span className="text-xs text-gray-400">{option.label}:</span>
                                                                <select
                                                                    value={option.value}
                                                                    onChange={(e) => handleOptionChange(indicator, option, e)}
                                                                >
                                                                    {option.options.map((option) => (
                                                                        <option value={option}>{option}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        );
                                                    default:
                                                        return <></>;
                                                }
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
